import AddIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import type { Category, ResolvedCategory } from "../../../api/types/accessTypes";
import useClientContext from "../../../context/useClientContext";
import { accessCategoryTagColors } from "@entrilia/shared/utilities/categoryColor";
import GeneralPageHeader from "../../common/GeneralPageHeader";
import EditAccessCategoryDialog from "./EditAccessCategoryDialog";
import AccessCategoriesGrid from "./AccessCategoriesGrid";

const getFreshTagColor = (categories: ResolvedCategory[]): string | undefined => {
  const usedColors = new Set(categories.map((c) => c.effectiveColor));
  return Object.entries(accessCategoryTagColors).find(([, color]) => !usedColors.has(color))?.[0];
};

const AccessCategoriesPage = () => {
  usePageTitle("Access Categories");

  const { investorPortalApp, hasPermissions, accessCategories, refreshAccessCategories } = useClientContext();

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryToEdit, setCategoryToEdit] = useState<Category>();
  const [creating, setCreating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const categories = useMemo(() => accessCategories.filter((c) => c.type === "Message"), [accessCategories]);

  const filteredRows = useMemo(
    () =>
      categories.filter(
        (c) =>
          c.name.toLocaleLowerCase().includes(searchTerm) ||
          (c.description ?? "").toLocaleLowerCase().includes(searchTerm)
      ),
    [categories, searchTerm]
  );

  const handleSearch = (value: string) => setSearchTerm(value.trim().toLocaleLowerCase());

  const refreshCategories = async () => {
    setIsRefreshing(true);
    await refreshAccessCategories();
    setIsRefreshing(false);
  };

  const handleCategorySaved = async () => {
    setCategoryToEdit(undefined);
    setCreating(false);
    await refreshCategories();
  };

  const canEditCategories = hasPermissions(["ManageInvestorPortalSettings"]);
  const investorPortalEnabled = Boolean(investorPortalApp);

  return (
    <>
      <GeneralPageHeader title="Access Categories">
        {canEditCategories && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            New Category
          </Button>
        )}
      </GeneralPageHeader>
      <Stack spacing={2.5} sx={{ py: 2.5, px: 3, height: "100%" }}>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
          <RecordCounter records={filteredRows.length} totalRecords={categories.length} />
          <SearchField debounceTimeMs={300} onSearch={handleSearch} />
        </Box>

        <AccessCategoriesGrid
          rows={filteredRows}
          loading={isRefreshing}
          investorPortalEnabled={investorPortalEnabled}
          onEditCategory={canEditCategories ? setCategoryToEdit : undefined}
        />
      </Stack>
      {creating && (
        <EditAccessCategoryDialog
          initialTagColor={getFreshTagColor(categories)}
          investorPortalEnabled={investorPortalEnabled}
          onClose={() => setCreating(false)}
          onSaved={handleCategorySaved}
        />
      )}
      {categoryToEdit && (
        <EditAccessCategoryDialog
          category={categoryToEdit}
          investorPortalEnabled={investorPortalEnabled}
          onClose={() => setCategoryToEdit(undefined)}
          onSaved={handleCategorySaved}
        />
      )}
    </>
  );
};

export default AccessCategoriesPage;
